import React, { useState, useMemo, useCallback, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../UserContext';
import api from '../api';
import MoaniDisplay from './MoaniDisplay';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  InputAdornment
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  ShoppingCart,
  Sell,
  SwapHoriz,
  AttachMoney,
  FilterList as FilterListIcon,
  Clear as ClearIcon
} from '@mui/icons-material';

const INITIAL_FILTERS = {
  type: '',
  item: '',
  counterParty: '',
  minQuantity: '',
  maxQuantity: '',
  minUnitPrice: '',
  maxUnitPrice: '',
  minTotal: '',
  maxTotal: '',
  dateFrom: '',
  dateTo: ''
};

function AuctionActivity({ userId }) {
  const navigate = useNavigate();
  const { selectUserById } = useUser();
  
  // Filter state
  const [filters, setFilters] = useState(INITIAL_FILTERS);

  // Get user data for display name
  const { data: userData } = useQuery({
    queryKey: ['user', userId],
    queryFn: async () => {
      if (!userId) return null;
      const response = await api.get(`/users/${userId}`);
      return response.data;
    },
    enabled: !!userId,
    staleTime: 60000 
  });

  const { data: auctionData, isLoading, error } = useQuery({
    queryKey: ['auctionActivity', userId],
    queryFn: async () => {
      if (!userId) return null;
      const response = await api.get(`/users/${userId}/auction-activity`);
      return response.data;
    },
    enabled: !!userId,
    staleTime: 60000 
  });

  // Stable reference to selectUserById to prevent re-renders
  const selectUserByIdRef = useRef(selectUserById);
  selectUserByIdRef.current = selectUserById;

  // Memoize format functions
  const formatMoani = useCallback((amount) => {
    if (!amount) return '0.00';
    return (Number(amount) / 1e18).toLocaleString(undefined, { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    });
  }, []);

  const formatDate = useCallback((dateString) => {
    return new Date(dateString).toLocaleString();
  }, []);

  // Filter transactions with stable dependencies
  const filteredTransactions = useMemo(() => {
    const { transactions = [] } = auctionData || {};
    if (transactions.length === 0) return [];
    
    return transactions.filter(transaction => {
      // Type filter
      if (filters.type && transaction.type !== filters.type) {
        return false;
      }
      
      // Item filter (name or ID)
      if (filters.item) {
        const itemLower = filters.item.toLowerCase();
        const assetTitle = (transaction.assetTitle || '').toLowerCase();
        const assetId = (transaction.assetId || '').toLowerCase();
        if (!assetTitle.includes(itemLower) && !assetId.includes(itemLower)) {
          return false;
        }
      }
      
      // Counter party filter (name or wallet)
      if (filters.counterParty) {
        const cpLower = filters.counterParty.toLowerCase();
        const cpName = (transaction.counterPartyName || '').toLowerCase();
        const cpWallet = (transaction.counterPartyWallet || '').toLowerCase();
        if (!cpName.includes(cpLower) && !cpWallet.includes(cpLower)) {
          return false;
        }
      }
      
      // Quantity filters
      const quantity = Number(transaction.stackSize || 0);
      if (filters.minQuantity && quantity < Number(filters.minQuantity)) {
        return false;
      }
      if (filters.maxQuantity && quantity > Number(filters.maxQuantity)) {
        return false;
      }
      
      // Unit price filters
      const unitPrice = Number(transaction.unitPrice || 0) / 1e18;
      if (filters.minUnitPrice && unitPrice < Number(filters.minUnitPrice)) {
        return false;
      }
      if (filters.maxUnitPrice && unitPrice > Number(filters.maxUnitPrice)) {
        return false;
      }
      
      // Total amount filters
      const totalAmount = Number(transaction.totalAmountRaw || 0) / 1e18;
      if (filters.minTotal && totalAmount < Number(filters.minTotal)) {
        return false;
      }
      if (filters.maxTotal && totalAmount > Number(filters.maxTotal)) {
        return false;
      }
      
      // Date filters
      if (filters.dateFrom) {
        const transactionDate = new Date(transaction.createdAt);
        const fromDate = new Date(filters.dateFrom);
        fromDate.setHours(0, 0, 0, 0);
        if (transactionDate < fromDate) {
          return false;
        }
      }
      if (filters.dateTo) {
        const transactionDate = new Date(transaction.createdAt);
        const toDate = new Date(filters.dateTo);
        toDate.setHours(23, 59, 59, 999);
        if (transactionDate > toDate) {
          return false;
        }
      }
      
      return true;
    });
  }, [auctionData, filters]);

  const handleFilterChange = useCallback((field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(INITIAL_FILTERS);
  }, []);

  const hasActiveFilters = useMemo(() => {
    return Object.values(filters).some(v => v !== '');
  }, [filters]);

  // Stable click handler using ref to avoid dependency issues
  const handleCounterPartyClick = useCallback((event, counterPartyId) => {
    if (!counterPartyId) return;
    
    event.preventDefault();
    event.stopPropagation();
    
    // Use ref to avoid dependency on selectUserById changing
    selectUserByIdRef.current(counterPartyId, navigate);
  }, [navigate]);

  if (!userId) {
    return (
      <Box display="flex" justifyContent="center" mt={4} sx={{ p: 2 }}>
        <Typography variant="h6">No user ID provided.</Typography>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress />
          <Typography>Loading auction activity...</Typography>
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load auction activity: {error.message}
      </Alert>
    );
  }

  const { overview = {}, transactions = [] } = auctionData || {};

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom>
        Auction House Activity - {userData?.name || 'User'}
      </Typography>

      {/* Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <TrendingUp sx={{ fontSize: 40, color: '#4caf50' }} />
                <Box>
                  <Typography color="text.secondary" variant="subtitle2">
                    Total Sales
                  </Typography>
                  <Typography variant="h6" color="white">
                    {overview.totalSalesCount || 0}
                  </Typography>
                  <MoaniDisplay 
                    amount={Number(overview.totalSalesAmount || 0) / 1e18} 
                    isWei={false}
                    variant="body2" 
                    sx={{ color: 'rgba(255,255,255,0.7)' }}
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <TrendingDown sx={{ fontSize: 40, color: '#f44336' }} />
                <Box>
                  <Typography color="text.secondary" variant="subtitle2">
                    Total Purchases
                  </Typography>
                  <Typography variant="h6" color="white">
                    {overview.totalPurchasesCount || 0}
                  </Typography>
                  <MoaniDisplay 
                    amount={Number(overview.totalPurchasesAmount || 0) / 1e18} 
                    isWei={false}
                    variant="body2" 
                    sx={{ color: 'rgba(255,255,255,0.7)' }}
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <SwapHoriz sx={{ fontSize: 40, color: '#fff' }} />
                <Box>
                  <Typography color="text.secondary" variant="subtitle2">
                    Net Balance
                  </Typography>
                  <MoaniDisplay 
                    amount={(Number(overview.totalSalesAmount) || 0) - (Number(overview.totalPurchasesAmount) || 0)} 
                    isWei={true}
                    variant="h6" 
                    sx={{ color: 'white' }}
                  />
                  <Typography variant="body2" color="rgba(255,255,255,0.7)">
                    Sales - Purchases
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <AttachMoney sx={{ fontSize: 40, color: '#fff' }} />
                <Box>
                  <Typography color="text.secondary" variant="subtitle2">
                    Total Volume
                  </Typography>
                  <MoaniDisplay 
                    amount={(Number(overview.totalSalesAmount) || 0) + (Number(overview.totalPurchasesAmount) || 0)} 
                    isWei={true}
                    variant="h6" 
                    sx={{ color: 'white' }}
                  />
                  <Typography variant="body2" color="rgba(255,255,255,0.7)">
                    Sales + Purchases
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Transaction History */}
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            Transaction History
          </Typography>
          {hasActiveFilters && (
            <Button
              size="small"
              startIcon={<ClearIcon />}
              onClick={clearFilters}
              variant="outlined"
            >
              Clear Filters ({filteredTransactions.length} of {transactions.length})
            </Button>
          )}
        </Box>
        <Divider sx={{ mb: 2 }} />
        
        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3, backgroundColor: 'background.default' }}>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <FilterListIcon />
            <Typography variant="subtitle2">Filters</Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select
                  value={filters.type}
                  label="Type"
                  onChange={(e) => handleFilterChange('type', e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="sale">Sale</MenuItem>
                  <MenuItem value="purchase">Purchase</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Item"
                placeholder="Name or ID"
                value={filters.item}
                onChange={(e) => handleFilterChange('item', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Counter Party"
                placeholder="Name or wallet"
                value={filters.counterParty}
                onChange={(e) => handleFilterChange('counterParty', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Min Quantity"
                type="number"
                value={filters.minQuantity}
                onChange={(e) => handleFilterChange('minQuantity', e.target.value)}
                InputProps={{
                  inputProps: { min: 0 }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Max Quantity"
                type="number"
                value={filters.maxQuantity}
                onChange={(e) => handleFilterChange('maxQuantity', e.target.value)}
                InputProps={{
                  inputProps: { min: 0 }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Min Unit Price (MOANI)"
                type="number"
                value={filters.minUnitPrice}
                onChange={(e) => handleFilterChange('minUnitPrice', e.target.value)}
                InputProps={{
                  inputProps: { min: 0, step: 0.01 }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Max Unit Price (MOANI)"
                type="number"
                value={filters.maxUnitPrice}
                onChange={(e) => handleFilterChange('maxUnitPrice', e.target.value)}
                InputProps={{
                  inputProps: { min: 0, step: 0.01 }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Min Total (MOANI)"
                type="number"
                value={filters.minTotal}
                onChange={(e) => handleFilterChange('minTotal', e.target.value)}
                InputProps={{
                  inputProps: { min: 0, step: 0.01 }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Max Total (MOANI)"
                type="number"
                value={filters.maxTotal}
                onChange={(e) => handleFilterChange('maxTotal', e.target.value)}
                InputProps={{
                  inputProps: { min: 0, step: 0.01 }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Date From"
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Date To"
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </Paper>
        
        {transactions.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            No auction transactions found
          </Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>Type</strong></TableCell>
                  <TableCell><strong>Item</strong></TableCell>
                  <TableCell><strong>Quantity</strong></TableCell>
                  <TableCell><strong>Unit Price</strong></TableCell>
                  <TableCell><strong>Total Amount</strong></TableCell>
                  <TableCell><strong>Counter Party</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTransactions.map((transaction) => (
                  <TableRow key={transaction.id}>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(transaction.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={transaction.type === 'sale' ? <Sell /> : <ShoppingCart />}
                        label={transaction.type === 'sale' ? 'Sale' : 'Purchase'}
                        color={transaction.type === 'sale' ? 'success' : 'primary'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {transaction.assetTitle || 'Unknown Item'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID: {transaction.assetId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {transaction.stackSize}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <MoaniDisplay 
                        amount={Number(transaction.unitPrice) / 1e18} 
                        isWei={false}
                        variant="body2" 
                        sx={{ fontFamily: 'monospace' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        fontWeight="bold"
                        sx={{ 
                          fontFamily: 'monospace',
                          color: transaction.type === 'sale' ? 'success.main' : 'error.main'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ mr: 0.5 }}>
                            {transaction.type === 'sale' ? '+' : '-'}
                          </Typography>
                          <MoaniDisplay 
                            amount={Number(transaction.totalAmountRaw) / 1e18} 
                            isWei={false}
                            variant="inherit" 
                            component="span"
                          />
                        </Box>
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {transaction.counterPartyId ? (
                        <Typography 
                          component="span"
                          variant="body2"
                          sx={{ 
                            cursor: 'pointer',
                            color: 'primary.main',
                            textDecoration: 'underline',
                            '&:hover': {
                              color: 'primary.dark'
                            }
                          }}
                          onClick={(event) => handleCounterPartyClick(event, transaction.counterPartyId)}
                          title="Click to view this user"
                        >
                          {transaction.counterPartyName || 'Unknown User'}
                        </Typography>
                      ) : (
                        <Typography variant="body2">
                          {transaction.counterPartyName || 'Unknown User'}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" display="block">
                        {transaction.counterPartyWallet ? 
                          `${transaction.counterPartyWallet.slice(0, 6)}...${transaction.counterPartyWallet.slice(-4)}` : 
                          'N/A'
                        }
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}

export default AuctionActivity;